import type { LoadAppPayload } from '@cogo/iframe-protocol'
import type { ReviewPatch } from './preview-review-store'

export type AppJsonPatch = {
  op: 'add' | 'remove' | 'replace'
  path: string
  value?: unknown
}

export interface BuildAppPatchesResult {
  patches: AppJsonPatch[]
  errors: string[]
}

type NodeSearchResult = {
  node: Record<string, unknown>
  path: string
}

const TEXT_KEYS = ['text', 'label', 'title', 'content', 'placeholder']
const IMAGE_KEYS = ['src', 'url', 'imageUrl', 'assetRef']

export function buildAppPatchesFromReviewPatches(
  reviewPatches: ReviewPatch[],
  appPayload?: LoadAppPayload,
): BuildAppPatchesResult {
  const patches: AppJsonPatch[] = []
  const errors: string[] = []
  for (const reviewPatch of reviewPatches) {
    const result = buildSinglePatch(reviewPatch, appPayload)
    if (result.patches) patches.push(...result.patches)
    if (result.error) errors.push(result.error)
  }
  return { patches, errors }
}

function buildSinglePatch(
  reviewPatch: ReviewPatch,
  appPayload?: LoadAppPayload,
): { patches?: AppJsonPatch[]; error?: string } {
  if (reviewPatch.op === 'replaceText') {
    const path = resolvePatchPath(reviewPatch.targetRef.path, appPayload, reviewPatch.targetRef.blockId, reviewPatch.targetRef.nodeId, TEXT_KEYS)
    if (!path) return { error: `Cannot resolve text target for ${reviewPatch.targetRef.label ?? reviewPatch.targetRef.nodeId ?? reviewPatch.targetRef.blockId ?? 'selection'}` }
    return {
      patches: [{
        op: 'replace',
        path,
        value: reviewPatch.nextText,
      }],
    }
  }

  if (reviewPatch.op === 'replaceImage') {
    const assetPath = resolvePatchPath(reviewPatch.targetRef.path, appPayload, reviewPatch.targetRef.blockId, reviewPatch.targetRef.nodeId, IMAGE_KEYS)
    if (!assetPath) return { error: `Cannot resolve image target for ${reviewPatch.targetRef.label ?? reviewPatch.targetRef.nodeId ?? reviewPatch.targetRef.blockId ?? 'selection'}` }
    const patches: AppJsonPatch[] = [
      { op: 'replace', path: assetPath, value: reviewPatch.nextAssetRef },
    ]
    const altPath = resolvePatchPath(undefined, appPayload, reviewPatch.targetRef.blockId, reviewPatch.targetRef.nodeId, ['alt', 'altText'])
    if (altPath) patches.push({ op: 'replace', path: altPath, value: reviewPatch.altText })
    return { patches }
  }

  const altPath = resolvePatchPath(reviewPatch.targetRef.path, appPayload, reviewPatch.targetRef.blockId, reviewPatch.targetRef.nodeId, ['alt', 'altText'])
  if (!altPath) return { error: `Cannot resolve alt-text target for ${reviewPatch.targetRef.label ?? reviewPatch.targetRef.nodeId ?? reviewPatch.targetRef.blockId ?? 'selection'}` }
  return {
    patches: [{
      op: 'replace',
      path: altPath,
      value: reviewPatch.nextAltText,
    }],
  }
}

function resolvePatchPath(
  explicitPath: string | undefined,
  appPayload: LoadAppPayload | undefined,
  blockId: string | undefined,
  nodeId: string | undefined,
  keys: string[],
): string | null {
  if (explicitPath?.startsWith('/')) return explicitPath
  if (!appPayload || !blockId || !nodeId) return null
  const block = appPayload.blocks?.[blockId]
  if (!isRecord(block)) return null
  const roots = [
    { value: readPath(block, ['ui', 'root']), path: `/blocks/${escapePointerPart(blockId)}/ui/root` },
    { value: readPath(block, ['root']), path: `/blocks/${escapePointerPart(blockId)}/root` },
  ]
  for (const root of roots) {
    if (!isRecord(root.value)) continue
    const found = findNode(root.value, root.path, nodeId)
    if (!found) continue
    const propertyPath = findPropertyPath(found.node, found.path, keys)
    if (propertyPath) return propertyPath
  }
  return null
}

function findPropertyPath(node: Record<string, unknown>, nodePath: string, keys: string[]): string | null {
  for (const key of keys) {
    if (typeof node[key] === 'string') return `${nodePath}/${escapePointerPart(key)}`
  }
  for (const containerKey of ['props', 'options', 'metadata']) {
    const nested = node[containerKey]
    if (!isRecord(nested)) continue
    for (const key of keys) {
      if (typeof nested[key] === 'string') {
        return `${nodePath}/${escapePointerPart(containerKey)}/${escapePointerPart(key)}`
      }
    }
  }
  return null
}

function findNode(node: Record<string, unknown>, nodePath: string, nodeId: string): NodeSearchResult | null {
  if (node.id === nodeId || node.nodeId === nodeId) return { node, path: nodePath }
  const children = node.children
  if (Array.isArray(children)) {
    for (let index = 0; index < children.length; index += 1) {
      const child = children[index]
      if (!isRecord(child)) continue
      const found = findNode(child, `${nodePath}/children/${index}`, nodeId)
      if (found) return found
    }
  }
  return null
}

function readPath(value: unknown, path: string[]): unknown {
  let cursor = value
  for (const part of path) {
    if (!isRecord(cursor)) return undefined
    cursor = cursor[part]
  }
  return cursor
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function escapePointerPart(value: string): string {
  return value.replace(/~/g, '~0').replace(/\//g, '~1')
}
